import React from 'react';

/**
 * Skeleton loading component — replaces spinners for content loading states.
 *
 * Usage:
 *   <Skeleton className="h-4 w-32" />              — single line
 *   <Skeleton className="h-10 w-10 rounded-full" /> — avatar
 *   <Skeleton className="h-40 w-full rounded-xl" /> — card
 *   <SkeletonCard />                                 — preset card skeleton
 *   <SkeletonTable rows={5} />                       — preset table skeleton
 */

export const Skeleton = ({ className = '' }) => (
  <div
    className={`animate-pulse bg-card-foreground/[0.06] rounded-lg ${className}`}
  />
);

export const SkeletonText = ({ lines = 3, className = '' }) => (
  <div className={`space-y-2.5 ${className}`}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton
        key={i}
        className={`h-3.5 ${i === lines - 1 ? 'w-3/5' : 'w-full'}`}
      />
    ))}
  </div>
);

export const SkeletonCard = ({ className = '' }) => (
  <div className={`bg-card border border-border rounded-xl p-5 space-y-4 ${className}`}>
    <div className="flex items-center gap-3">
      <Skeleton className="h-10 w-10 rounded-full flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-2/5" />
        <Skeleton className="h-3 w-3/5" />
      </div>
    </div>
    <SkeletonText lines={2} />
  </div>
);

export const SkeletonRow = () => (
  <div className="flex items-center gap-4 py-3 px-4">
    <Skeleton className="h-9 w-9 rounded-xl flex-shrink-0" />
    <div className="flex-1 space-y-1.5">
      <Skeleton className="h-3.5 w-2/5" />
      <Skeleton className="h-3 w-1/4" />
    </div>
    <Skeleton className="h-4 w-16" />
  </div>
);

export const SkeletonTable = ({ rows = 5 }) => (
  <div className="divide-y divide-border">
    {Array.from({ length: rows }).map((_, i) => (
      <SkeletonRow key={i} />
    ))}
  </div>
);

export const SkeletonStats = ({ count = 4 }) => (
  <div className={`grid grid-cols-2 ${count > 2 ? 'lg:grid-cols-4' : ''} gap-4`}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="bg-card border border-border rounded-xl p-4 space-y-3">
        <Skeleton className="h-3 w-1/2" />
        <Skeleton className="h-7 w-3/5" />
        <Skeleton className="h-2.5 w-2/3" />
      </div>
    ))}
  </div>
);

export const SkeletonPage = () => (
  <div className="space-y-6">
    <SkeletonStats count={4} />
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <SkeletonCard />
      <SkeletonCard />
    </div>
    <SkeletonTable rows={5} />
  </div>
);

export default Skeleton;
